import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { FileDown, Mail } from "lucide-react";
import { api, errMsg, eur, fmtDateTime, openBlob } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { PageTitle, Chip, Field, SearchBox, matchQ, focusRing } from "../components/common";
import { useFocusParam, useScrollTo } from "../hooks/useFocusParam";
import { MonthField } from "../components/DateFields";
import { currentPeriod } from "../lib/labels";
import { useLang } from "../lib/i18n";

const STATUS = { provisoire: ["amber", "Provisoire — en attente de contrôle — ne vaut pas facture"], controle: ["green", "Contrôlé — en attente de validation"], valide: ["blue", "Validé par le gestionnaire"] };

function Dispute({ pf, reload, staff }) {
  const { t } = useLang();
  const [msg, setMsg] = useState("");
  const send = async () => {
    try { await api.post(`/proformas/${pf.id}/dispute`, { message: msg }); setMsg(""); toast.success("Désaccord signalé"); reload(); } catch (e) { toast.error(errMsg(e)); }
  };
  const resolve = async (id) => { await api.post(`/proformas/${pf.id}/disputes/${id}/resolve`); reload(); };
  return (
    <div className="mt-2 space-y-1">
      {(pf.disputes || []).map((x) => (
        <div key={x.id} className="text-xs bg-amber-50 rounded p-2 flex gap-2">{x.by} : {x.message} {x.resolved ? <Chip tone="green">résolu</Chip> : staff && <button className="underline" onClick={() => resolve(x.id)}>Marquer résolu</button>}</div>
      ))}
      {!staff && pf.status !== "valide" && (
        <div className="flex gap-2"><input className="tl-input" placeholder={t("pf.dispute")} value={msg} onChange={(e) => setMsg(e.target.value)} data-testid={`dispute-input-${pf.version}`} />
          <button className="tl-btn tl-btn-dark tl-btn-sm" disabled={!msg.trim()} onClick={send} data-testid={`dispute-submit-${pf.version}`}>{t("c.send")}</button></div>
      )}
    </div>
  );
}

export default function ProFormas() {
  const { user } = useAuth();
  const { t } = useLang();
  const staff = user.role !== "soustraitant";
  const [companies, setCompanies] = useState([]);
  const [company, setCompany] = useState(user.company_id || "");
  const [period, setPeriod] = useState(currentPeriod());
  const [list, setList] = useState([]);
  const [q, setQ] = useState("");
  const { pf: focusPf } = useFocusParam(["pf"]);
  const shown = list.filter((pf) => matchQ(q, pf.ref, pf.company_name, pf.period, STATUS[pf.status]?.[1]));
  useScrollTo(focusPf, shown.length);
  const load = useCallback(() => api.get("/proformas", { params: { company_id: company || undefined } }).then((r) => setList(r.data)), [company]);
  useEffect(() => {
    if (staff) api.get("/companies").then((r) => setCompanies(r.data));
  }, [staff]);
  useEffect(() => {
    load();
  }, [load]);
  const generate = async () => {
    try { const { data } = await api.post("/proformas", { company_id: company, period }); toast.success(`Pro forma ${data.ref} v${data.version} généré`); load(); } catch (e) { toast.error(errMsg(e)); }
  };
  const validate = async (id) => {
    try { await api.post(`/proformas/${id}/validate`); toast.success("Pro forma validé et envoyé au sous-traitant"); load(); } catch (e) { toast.error(errMsg(e)); }
  };
  const [mail, setMail] = useState({ from: currentPeriod(), to: currentPeriod() });
  const sendMail = async () => {
    try {
      const { data } = await api.post("/proformas/email", { company_id: company || null, period_from: mail.from, period_to: mail.to });
      toast.success(`${data.count} pro forma(s) envoyée(s) par e-mail`);
    } catch (e) { toast.error(errMsg(e)); }
  };
  return (
    <div>
      <PageTitle title={t("pf.title")} sub={t("pf.sub")}><SearchBox value={q} onChange={setQ} testid="proformas-search" /></PageTitle>
      <div className="tl-card flex flex-wrap gap-3 items-end mb-5">
        {staff && (
          <Field label={t("c.company")} className="w-56">
            <select className="tl-select" value={company} onChange={(e) => setCompany(e.target.value)} data-testid="proforma-company">
              <option value="">{t("c.choose")}</option>{companies.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
            </select>
          </Field>
        )}
        <Field label={t("c.period")} className="w-44"><MonthField value={period} onChange={setPeriod} testid="proforma-period" /></Field>
        <button className="tl-btn tl-btn-primary" disabled={!company || !period} onClick={generate} data-testid="generate-proforma-button">{t("pf.get")}</button>
      </div>
      <div className="tl-card flex flex-wrap gap-3 items-end mb-5" data-testid="proforma-email-box">
        <Field label="Du mois" className="w-44"><MonthField value={mail.from} onChange={(v) => setMail({ ...mail, from: v })} testid="proforma-email-from" /></Field>
        <Field label="Au mois" className="w-44"><MonthField value={mail.to} onChange={(v) => setMail({ ...mail, to: v })} testid="proforma-email-to" /></Field>
        <button className="tl-btn tl-btn-dark" disabled={!company || !mail.from || !mail.to} onClick={sendMail} data-testid="proforma-email-button">
          <Mail size={16} /> {staff ? "Envoyer par e-mail au sous-traitant" : "Recevoir par e-mail"}
        </button>
      </div>
      <div className="space-y-3">
        {shown.map((pf) => (
          <div key={pf.id} data-focus-id={pf.id} className={`tl-card tl-fade ${focusRing(focusPf === pf.id)}`} data-testid={`proforma-${pf.ref}-v${pf.version}`}>
            <div className="flex flex-wrap items-center gap-2">
              <span className="tl-mono font-semibold">{pf.ref} · v{pf.version}</span>
              <span className="text-sm">{pf.company_name} · {pf.period}</span>
              <Chip tone={STATUS[pf.status][0]} testid={`proforma-status-${pf.version}`}>{STATUS[pf.status][1]}</Chip>
              <div className="ml-auto flex gap-2">
                <button className="tl-btn tl-btn-ghost tl-btn-sm" onClick={() => openBlob(`/proformas/${pf.id}/download`, `${pf.ref}-v${pf.version}.pdf`, true)} data-testid={`download-proforma-${pf.version}`}><FileDown size={16} /> PDF</button>
                {staff && pf.status === "controle" && <button className="tl-btn tl-btn-dark tl-btn-sm" onClick={() => validate(pf.id)} data-testid={`validate-proforma-${pf.version}`}>{t("c.validate")}</button>}
              </div>
            </div>
            <div className="text-sm mt-1">{t("pf.subtotal")} : <b className="tl-mono" data-testid={`proforma-total-${pf.version}`}>{eur(pf.totals.known_subtotal)}</b> · {pf.totals.lines} ligne(s)
              {pf.totals.lines_to_confirm > 0 && <span className="text-amber-700"> · total incomplet, {pf.totals.lines_to_confirm} ligne(s) à confirmer</span>} · {fmtDateTime(pf.created_at)}</div>
            <Dispute pf={pf} reload={load} staff={staff} />
          </div>
        ))}
        {!shown.length && <div className="tl-card text-slate-500">{t("pf.none")}</div>}
      </div>
    </div>
  );
}
